/**
 * Eventi — contratto `modello-dati.md` §2.2 (LA verità). Campi fissi: event_id, type, user_id,
 * timestamp (ISO 8601), schema_version, device_id, data + ID del soggetto top-level.
 *
 * `schema_version` (migrazioni del formato, pratica Muse) e `device_id` (tie-break deterministico
 * del merge a parità di timestamp) sono **ratificati: D48** (2026-09-24) — formalizzati in
 * modello-dati §2.2/§2.3. Nessun campo si aggiunge senza D.
 */
import { newId } from "./ids";

export const SCHEMA_VERSION = 1; // D48

export interface HyprEvent {
  event_id: string;
  type: string;
  user_id: string;
  timestamp: string; // ISO 8601 (D12)
  schema_version: number; // D48
  device_id: string; // D48 (tie-break merge)
  data: Record<string, unknown>;
  [subjectKey: string]: unknown; // chiavi di contesto top-level (session_id, set_id, ...)
}

/**
 * Tombstone (contratto §2.3.5): `*.deleted` = "questo record è cancellato"; `user.erased` =
 * tombstone sull'intero namespace account (irreversibile, D24). **D55 (2026-09-26, voto
 * proprietario):** la semantica COMPLETA del merge (il tombstone vince sugli update più vecchi,
 * un update più recente fa risorgere il record) si implementa a M7, insieme alla UI di
 * cancellazione — la tabella eventi §2.2 la schedula già come "fix M7". Fin qui i tombstone si
 * RICONOSCONO, si conservano verbatim (append-only: un roundtrip non li perde) e si DICHIARANO
 * nel resoconto d'import: mai contati come "eventi nuovi" in silenzio (blind review M2, MAJOR 1).
 */
export function isTombstone(type: string): boolean {
  return type.endsWith(".deleted") || type === "user.erased";
}

export function isoNow(): string {
  // millisecondi INTATTI (D49): servono al tie-break (timestamp, device_id, event_id)
  return new Date().toISOString();
}

/**
 * Prossimo `set_number` dell'esercizio nella seduta (contratto §2.2: "1, 2, 3...").
 * DERIVATO dagli eventi, mai da contatori in memoria: dopo un kill il progressivo DEVE continuare
 * (regressione trovata nel kill-test iPhone 2026-09-24: due set tornavano entrambi `set_number: 1`).
 */
export function nextSetNumber(events: HyprEvent[], session_id: string, exercise_id: string): number {
  let n = 0;
  for (const e of events) {
    if (e.type === "set.created" && e["session_id"] === session_id && e.data["exercise_id"] === exercise_id) n += 1;
  }
  return n + 1;
}

/**
 * Confronto per l'ordine TOTALE D49: (timestamp, device_id, event_id). UNA implementazione
 * sola, condivisa da export e dal worker del DB: l'ordine della tabella UI e quello export
 * non possono divergere (blind review M0, MAJOR).
 *
 * PERCHÉ non i confronti tra stringhe: due timestamp con precisione mista (storici a secondi
 * vs millisecondi D49) NON ordinano cronologicamente come stringhe — "…00.500Z" < "…00Z"
 * lessicalmente ("." < "Z") mentre viene cronologicamente DOPO. I timestamp si confrontano
 * quindi come ISTANTI (epoch ms). I tie-break usano il confronto di codice (`<`), mai
 * `localeCompare`: l'ordine deve essere identico su ogni device, qualunque sia la sua locale.
 * Gli eventi storici PRE-D48 senza `device_id` ordinano come stringa vuota (deterministico
 * comunque: con `event_id` unico l'ordine resta totale, dedup §2.3).
 */
export function compareEvents(a: HyprEvent, b: HyprEvent): number {
  const ta = Date.parse(a.timestamp);
  const tb = Date.parse(b.timestamp);
  if (Number.isNaN(ta) || Number.isNaN(tb)) {
    // timestamp non ISO (fuori contratto): deterministico comunque, mai crash in ordinamento
    return a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0;
  }
  if (ta !== tb) return ta < tb ? -1 : 1;
  const da = a.device_id ?? "";
  const db = b.device_id ?? "";
  if (da !== db) return da < db ? -1 : 1;
  if (a.event_id !== b.event_id) return a.event_id < b.event_id ? -1 : 1;
  return 0;
}

export function makeEvent(args: {
  type: string;
  user_id: string;
  device_id: string;
  data: Record<string, unknown>;
  // chiavi di contesto top-level (contratto §2.2): set.created porta session_id + set_id
  subject?: { key: string; value: string } | Array<{ key: string; value: string }>;
  timestamp?: string;
}): HyprEvent {
  const ev: HyprEvent = {
    event_id: newId("ev"),
    type: args.type,
    user_id: args.user_id,
    timestamp: args.timestamp ?? isoNow(),
    schema_version: SCHEMA_VERSION,
    device_id: args.device_id,
    data: args.data,
  };
  if (args.subject) {
    for (const s of Array.isArray(args.subject) ? args.subject : [args.subject]) ev[s.key] = s.value;
  }
  return ev;
}

/** Parse di una riga JSONL → evento valido o errore descritto (mai eccezione nuda).
 *
 * Validazione MINIMA deliberata (anti-lock-in, regola 7): un export prodotto da una versione
 * futura dell'app deve restare importabile — quindi i `type` ignoti NON si rifiutano (alla
 * proiezione si ignorano) e `schema_version`/`device_id` si validano solo se presenti (gli
 * eventi PRE-D48 del 24/09 non li hanno e l'append-only non li riscrive). Quello che NON può
 * passare è la spazzatura: righe non-JSON, campi fissi mancanti, timestamp non ISO.
 */
export function parseEvent(line: string): { ok: true; event: HyprEvent } | { ok: false; error: string } {
  let obj: unknown;
  try {
    obj = JSON.parse(line);
  } catch {
    return { ok: false, error: "riga non-JSON" };
  }
  if (typeof obj !== "object" || obj === null) return { ok: false, error: "riga non-oggetto" };
  const e = obj as Record<string, unknown>;
  for (const field of ["event_id", "type", "user_id", "timestamp"] as const) {
    if (typeof e[field] !== "string") return { ok: false, error: `campo mancante: ${field}` };
  }
  if (typeof e["data"] !== "object" || e["data"] === null) return { ok: false, error: "campo mancante: data" };
  // ISO 8601 (D12): timestamp non parseabile = riga corrotta, non un evento (in ordine D49
  // finirebbe in fondo con un confronto arbitrario)
  if (Number.isNaN(Date.parse(e["timestamp"] as string))) return { ok: false, error: "timestamp non ISO 8601" };
  // D48: quando presenti devono essere del tipo giusto — assenti = eventi storici PRE-D48, ok
  if (e["schema_version"] !== undefined && typeof e["schema_version"] !== "number") {
    return { ok: false, error: "schema_version non numerico" };
  }
  if (e["device_id"] !== undefined && typeof e["device_id"] !== "string") {
    return { ok: false, error: "device_id non stringa" };
  }
  return { ok: true, event: obj as HyprEvent };
}
